import { useState, useMemo, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  captureAndSendEmail,
  precaptureFlowEmails,
  type EmailDetail,
} from "@/lib/email-render";
import { groupAnglesByFamily } from "@/lib/angle-groups";
import {
  usePickerSearch,
  PickerSearchHeader,
  PickerNoMatches,
} from "@/lib/picker-search";
import { thumbUrl, thumbFallback } from "@/lib/img-thumb";
import {
  Mail,
  Wand2,
  Megaphone,
  Repeat,
  Sparkles,
  BookOpen,
  Search,
  Image as ImageIcon,
  Type as TypeIcon,
  PanelBottom,
  ListChecks,
  Star,
  LayoutGrid,
  Swords,
  Columns3,
  Lightbulb,
  Flag,
  BadgePercent,
  MousePointerClick,
  ArrowUpDown,
  Pencil,
  Send,
  Copy as CopyIcon,
  Layers,
  Loader2,
  Trash2,
  RotateCw,
  Users,
  Package,
  Percent,
  Folder,
  Hash,
  Library,
  LayoutTemplate,
  Link2,
  X,
  Eye,
  Check,
} from "lucide-react";
import { KlaviyoConnectionBadge } from "@/components/email-express/klaviyo-connection";
import {
  UniversalLibrary,
  UNIVERSAL_ASSET_TYPE_LIST,
  type UniversalAsset,
} from "@/components/email-express/universal-library";

type Persona = { id: string; name: string; imageUrl?: string | null };
type Product = { id: string; name: string; mediaJson?: string[] | null };
type NarrativeAngle = {
  id: string;
  name: string;
  family?: string | null;
  summary?: string | null;
  personaId?: string | null;
  productId?: string | null;
  status?: string | null;
};
type Project = { id: string; name: string };

type EmailRow = {
  id: string;
  name: string;
  type: "campaign" | "flow";
  status: "draft" | "sent_to_klaviyo";
  personaNameSnapshot?: string | null;
  narrativeAngleLabel?: string | null;
  flowType?: string | null;
  flowSequence?: number | null;
  createdAt?: string | null;
  // Background copy-generation progress (progressive load). null on legacy rows.
  copyStatus?: "awaiting_template" | "pending" | "ready" | "failed" | null;
};

// Task #1741 — paginated envelope from the list endpoint.
type EmailListPage = {
  rows: EmailRow[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
};
const EMAIL_PAGE_SIZE = 15;

// A flow CONTAINER (with its ordered child emails) as returned by /flows.
type FlowRow = {
  id: string;
  name: string;
  flowType: string;
  status: "draft" | "sending" | "sent_to_klaviyo" | "send_failed";
  createdAt?: string | null;
  emails: EmailRow[];
};

type Stats = {
  total: number;
  campaigns: number;
  flows: number;
  drafts: number;
  sent: number;
};

// One entry from GET /email-express/preset-templates (registry def + R2 art).
type PresetTemplateItem = {
  key: string;
  name: string;
  description: string;
  useCase: string;
  // Rollout gate: disabled templates show as "Coming soon" and can't be picked.
  enabled: boolean;
  // Block-driven mode (Task #1032): the standard Email blocks UI stays active
  // and drives generation; the template supplies layout patterns only. These
  // templates are selectable even while enabled=false.
  blockDriven?: boolean;
  // V2-parity templates carry a fixed block sequence — selecting the template
  // pre-checks these optional blocks.
  blocks?: string[] | null;
  referenceImageUrl: string | null;
  thumbnailUrl: string | null;
  assets: { grainUrl?: string | null } | null;
  inputs: {
    key: string;
    label: string;
    type: "text" | "textarea" | "product";
    required: boolean;
    placeholder?: string;
    help?: string;
  }[];
};

type PickerOption = { value: string; label: string; imageUrl?: string | null; description?: string | null };
type PickerGroup = { label: string; options: PickerOption[] };

const FLOW_LABELS: Record<string, string> = {
  welcome: "Welcome series",
  cart_abandonment: "Cart abandonment",
  browse_abandonment: "Browse abandonment",
  retention: "Retention–winback",
};

// Ordered flow types for the create-form selector (drives the Klaviyo trigger).
const FLOW_TYPE_VALUES = [
  "welcome",
  "cart_abandonment",
  "browse_abandonment",
  "retention",
] as const;

const LOCKED_BLOCKS = [
  { key: "hero", label: "Hero + CTA", desc: "Headline, image, button", icon: ImageIcon },
  { key: "body", label: "Body copy", desc: "~2 paragraphs", icon: TypeIcon },
  { key: "footer", label: "Footer", desc: "Logo, unsubscribe", icon: PanelBottom },
];

const OPTIONAL_BLOCKS = [
  { key: "selling_points", label: "Selling points", desc: "Bulleted benefits", icon: ListChecks },
  { key: "reviews", label: "Reviews", desc: "2–3 testimonials", icon: Star },
  { key: "product_list", label: "Product list", desc: "Best sellers grid", icon: LayoutGrid },
  { key: "us_vs_them", label: "Us vs. them", desc: "Quick us/them contrast", icon: Swords },
  { key: "comparison_table", label: "Comparison table", desc: "Feature-by-feature grid", icon: Columns3 },
  { key: "did_you_know", label: "Did you know", desc: "Ingredient spotlight", icon: Lightbulb },
  { key: "logo_banner", label: "Logo banner", desc: "Top-of-email bar", icon: Flag },
  { key: "image_only", label: "Image only", desc: "Full-width product photo", icon: ImageIcon },
  { key: "image_discount", label: "Image + discount", desc: "Photo + code band", icon: BadgePercent },
  { key: "cta", label: "CTA button", desc: "Standalone button band", icon: MousePointerClick },
];

const NONE_SALE = "__none__";
const SALE_20 = "__20off__";
const SALE_CUSTOM = "__custom__";
// Saved Sales & Discounts entries join the picker as `sale:<id>` values.
const SAVED_SALE_PREFIX = "sale:";

// Universal-content picker sentinels (Radix Select disallows empty-string values).
const UNIVERSAL_NONE = "__none__";

// Universal library is feature-flagged off for now (code kept for later enablement).
const SHOW_UNIVERSAL_LIBRARY = false;

const SHOW_BLOCK_PICKER = false;
function formatCreated(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function EmailExpressPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const isMaster = !!user?.isMaster;
  const [, navigate] = useLocation();
  const [generateError, setGenerateError] = useState<string | null>(null);
  // A 401 anywhere on this page (generate mutation or a background query)
  // means the session expired: cached queries keep the page LOOKING fine while
  // every action silently fails. Flip this to show a re-login banner instead.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);

  // ---- Generator state -----------------------------------------------------
  const [emailType, setEmailType] = useState<"campaign" | "flow">("campaign");
  const [flowType, setFlowType] = useState<string>("welcome");
  const [personaId, setPersonaId] = useState("");
  const [productId, setProductId] = useState("");
  const [angleId, setAngleId] = useState("");
  const [angleMode, setAngleMode] = useState<"saved" | "custom">("saved");
  const [topicText, setTopicText] = useState("");
  const [saleChoice, setSaleChoice] = useState(NONE_SALE);
  const [saleCode, setSaleCode] = useState("");
  const [salePercentage, setSalePercentage] = useState("");
  // Free-text offer description ("Free bundle with any sandwich") — usable on
  // its own or combined with a percentage.
  const [saleDescription, setSaleDescription] = useState("");
  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);
  const [projectId, setProjectId] = useState("");
  const [subjectCount, setSubjectCount] = useState("4");
  // Optional default CTA link — carried into linkConfig.sitewideUrl on the
  // generated email (same field the editor's Links panel edits).
  const [ctaLink, setCtaLink] = useState("");
  // Multi-email flows: how many emails to auto-generate (only used for flows).
  const [emailCount, setEmailCount] = useState("3");
  // Preset template (campaign-only, master-gated). "" = no preset — the
  // existing archetype flow, byte-identical. Templates carry no extra inputs
  // today (everything derives from the standard settings); presetInputs is the
  // generic form state should a future template define registry input defs.
  const [presetKey, setPresetKey] = useState("");
  const [presetInputs, setPresetInputs] = useState<Record<string, string>>({});
  // Template card popup preview (Task #1032): clicking a card opens a large
  // reference-image preview with a confirm action instead of selecting inline.
  const [previewTemplate, setPreviewTemplate] =
    useState<PresetTemplateItem | null>(null);

  // ---- Library state -------------------------------------------------------
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "campaign" | "flow" | "sent" | "draft">("all");
  const [newestFirst, setNewestFirst] = useState(true);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // Task #1741 — server-side pagination over the (very wide) email table.
  const [page, setPage] = useState(1);

  // ---- Top-level tab + Universal-content attach state ----------------------
  const [activeTab, setActiveTab] = useState<"generate" | "universal">("generate");
  const [universalSel, setUniversalSel] = useState<Record<string, string>>({});

  // ---- Brand-level footer settings (Task #1899) -----------------------------
  // Physical address + unsubscribe link that every email footer shows. Lives
  // here on the main page (the Universal Library tab is feature-flagged off).
  const [footerSettingsOpen, setFooterSettingsOpen] = useState(false);
  const [footerAddress, setFooterAddress] = useState("");
  const [footerUnsubUrl, setFooterUnsubUrl] = useState("");
  const { data: footerSettings } = useQuery<{
    physicalAddress: string;
    unsubscribeUrl: string;
  }>({
    queryKey: [`/api/brands/${brandId}/email-express/settings`],
    enabled: !!brandId,
  });
  // Seed the dialog fields whenever it opens (fresh from the server value).
  useEffect(() => {
    if (footerSettingsOpen) {
      setFooterAddress(footerSettings?.physicalAddress ?? "");
      setFooterUnsubUrl(footerSettings?.unsubscribeUrl ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerSettingsOpen]);
  const saveFooterSettingsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "PUT",
        `/api/brands/${brandId}/email-express/settings`,
        {
          physicalAddress: footerAddress.trim(),
          unsubscribeUrl: footerUnsubUrl.trim(),
        },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/email-express/settings`],
      });
      setFooterSettingsOpen(false);
      toast({
        title: "Footer settings saved",
        description:
          "Every email footer now uses this address and unsubscribe link.",
      });
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't save footer settings",
        description: e?.message || "Please try again.",
        variant: "destructive",
      }),
  });

  // ---- Data ----------------------------------------------------------------
  const { data: brand } = useQuery<{ id: string; name: string }>({
    queryKey: ["/api/brands", brandId],
    enabled: !!brandId,
  });
  const { data: personas = [] } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  // Saved Sales & Discounts join the sale picker; a non-percentage sale
  // pre-fills the free-text offer description from its promotion copy.
  const { data: savedSales = [] } = useQuery<
    {
      id: string;
      name: string;
      saleType: string;
      discountValue?: string | null;
      promotionSummary?: string | null;
      customerFacingCopy?: string | null;
      promoCode?: string | null;
    }[]
  >({
    queryKey: [`/api/brands/${brandId}/sales-discounts`],
    enabled: !!brandId,
  });
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: angles = [] } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: [`/api/brands/${brandId}/projects`],
    enabled: !!brandId,
  });
  // Task #1741 — paginated list endpoint. The query key keeps the plain URL
  // as its first element so existing prefix invalidations still refresh every
  // page; the page number rides along as a second element.
  const {
    data: emailPage,
    isLoading: loadingEmails,
    error: emailsError,
  } = useQuery<EmailListPage>({
    queryKey: [`/api/brands/${brandId}/email-express/emails`, page],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/brands/${brandId}/email-express/emails?page=${page}&limit=${EMAIL_PAGE_SIZE}`,
      );
      return (await res.json()) as EmailListPage;
    },
    enabled: !!brandId,
    // While any email is still being generated in the background (progressive
    // load), poll so its status badge flips to ready/failed without a refresh.
    refetchInterval: (query) =>
      (query.state.data?.rows ?? []).some((e) => e.copyStatus === "pending")
        ? 4000
        : false,
  });
  const emails = emailPage?.rows ?? [];
  const totalEmails = emailPage?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalEmails / EMAIL_PAGE_SIZE));
  // Keep the user on a valid page when the last row of the final page is
  // removed (e.g. after a delete elsewhere) and the page count shrinks.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const {
    data: flows = [],
    isLoading: loadingFlows,
    error: flowsError,
  } = useQuery<FlowRow[]>({
    queryKey: [`/api/brands/${brandId}/email-express/flows`],
    enabled: !!brandId,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((f) =>
        (f.emails ?? []).some((e) => e.copyStatus === "pending"),
      )
        ? 4000
        : false,
  });
  // Detect a stale-session state from the page's own queries: the default
  // query fn throws "401: …" errors, and the polling list queries are the
  // first to notice an expired cookie. Refresh the auth-user query too so the
  // app-level auth handling reacts.
  useEffect(() => {
    const errs = [emailsError, flowsError].filter(Boolean) as Error[];
    if (errs.some((e) => /^401\b/.test(e.message))) {
      setSessionExpired(true);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    }
  }, [emailsError, flowsError]);

  const { data: stats } = useQuery<Stats>({
    queryKey: [`/api/brands/${brandId}/email-express/stats`],
    enabled: !!brandId,
  });
  const { data: assets = [] } = useQuery<UniversalAsset[]>({
    queryKey: [`/api/brands/${brandId}/email-express/assets`],
    enabled: !!brandId,
  });
  // Preset template registry (master-gated server-side; the query only runs
  // for masters so non-masters see zero change).
  const { data: presetTemplates = [] } = useQuery<PresetTemplateItem[]>({
    queryKey: [`/api/brands/${brandId}/email-express/preset-templates`],
    enabled: !!brandId && isMaster,
  });
  const selectedPreset = useMemo(
    () => presetTemplates.find((t) => t.key === presetKey) ?? null,
    [presetTemplates, presetKey],
  );
  // Fixed-sequence templates (the V2-parity set) pre-check their own optional
  // blocks whenever the template becomes selected — via the card OR the preview
  // dialog. Blocks stay user-toggleable afterwards; deselecting the template
  // leaves the current selection alone.
  useEffect(() => {
    const tplBlocks = selectedPreset?.blocks;
    if (Array.isArray(tplBlocks) && tplBlocks.length) {
      setSelectedBlocks(
        tplBlocks.filter((b) => !["hero", "body", "footer"].includes(b)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset?.key]);

  // Required per-template inputs must be filled before generating.
  const presetInputsReady = useMemo(() => {
    if (!selectedPreset) return true;
    return selectedPreset.inputs.every(
      (i) => !i.required || (presetInputs[i.key] ?? "").trim().length > 0,
    );
  }, [selectedPreset, presetInputs]);
  const assetsByType = useMemo(() => {
    const m: Record<string, UniversalAsset[]> = {};
    for (const a of assets) (m[a.type] ||= []).push(a);
    return m;
  }, [assets]);
  // Seed each type's picker with its default asset once, without clobbering an
  // explicit user choice (including an explicit "None").
  useEffect(() => {
    if (!assets.length) return;
    setUniversalSel((prev) => {
      const next = { ...prev };
      for (const a of assets) {
        if (a.isDefault && next[a.type] === undefined) next[a.type] = a.id;
      }
      return next;
    });
  }, [assets]);

  // Narrative angles filtered by the persona+product combination.
  const availableAngles = useMemo(
    () =>
      angles.filter((a) => {
        if (a.status === "archived") return false;
        if (personaId && a.personaId !== personaId) return false;
        return !productId || a.productId === productId;
      }),
    [angles, personaId, productId],
  );
  const angleGroups = useMemo<PickerGroup[]>(
    () =>
      groupAnglesByFamily(availableAngles).map((g) => ({
        label: g.label,
        options: g.angles.map((a) => ({ value: a.id, label: a.name, description: a.summary || null })),
      })),
    [availableAngles],
  );

  const anglePlaceholder = !productId
    ? "Pick a product first"
    : availableAngles.length === 0
      ? "No saved angles yet — try Describe topic"
      : "Choose a saved angle";

  const toggleBlock = (key: string) =>
    setSelectedBlocks((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  // Required-field gating. For a single campaign, a product is the only
  // required input. For a flow, product is OPTIONAL — a sequence can be drafted
  // around the brand voice + persona + flow intent alone — so only the flow
  // type is required. Persona, angle, and sale are always optional and additive.
  // When the template picker is available and freeform is disabled, a
  // campaign REQUIRES a selected template. Users without the picker (no
  // templates / not master) keep the freeform path.
  const templateRequired =
    !ALLOW_FREEFORM && isMaster && presetTemplates.length > 0;
  const canGenerate =
    emailType === "flow"
      ? !!flowType
      : !!productId &&
        (!templateRequired || !!presetKey) &&
        (!presetKey || presetInputsReady);
  // Preset templates apply to single campaigns only; a stale selection is
  // ignored (not sent) while the Flow toggle is active.
  const presetActive = emailType === "campaign" && !!presetKey;
  // Block-driven template mode (Task #1032): the standard Email blocks UI
  // stays visible and drives generation (mandatory hero/body/footer enforced
  // server-side).
  const presetBlockDriven = presetActive && !!selectedPreset?.blockDriven;

  const generateMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        type: emailType,
        flowType: emailType === "flow" ? flowType : null,
        personaId: personaId || null,
        productId,
        narrativeAngleId: angleMode === "saved" ? angleId : "",
        customTopic: angleMode === "custom" ? topicText.trim() : "",
        blocks: selectedBlocks,
        subjectLineCount: Number(subjectCount),
        projectId: projectId || null,
      };
      // Optional default CTA link → the generated email's linkConfig.sitewideUrl.
      if (ctaLink.trim()) body.ctaLink = ctaLink.trim();
      // Preset template (campaign-only). Absent key keeps the payload — and
      // the server flow — byte-identical to today.
      if (emailType === "campaign" && presetKey && selectedPreset) {
        body.presetTemplateKey = presetKey;
        const inputsOut: Record<string, unknown> = {};
        for (const def of selectedPreset.inputs) {
          const raw = (presetInputs[def.key] ?? "").trim();
          if (!raw) continue;
          inputsOut[def.key] = raw;
        }
        body.presetInputs = inputsOut;
      }
      const universalAssets: Record<string, string> = {};
      for (const { type } of UNIVERSAL_ASSET_TYPE_LIST) {
        const v = universalSel[type];
        if (v && v !== UNIVERSAL_NONE) universalAssets[type] = v;
      }
      body.universalAssets = universalAssets;
      if (saleChoice === SALE_20) {
        body.saleDiscountLabel = "20% off sitewide";
        body.salePercentage = 20;
      } else if (saleChoice === SALE_CUSTOM || saleChoice.startsWith(SAVED_SALE_PREFIX)) {
        // The label carries the free-text offer description when present;
        // otherwise the legacy "Custom code" sentinel keeps percentage-only
        // submissions byte-identical to before.
        body.saleDiscountLabel = saleDescription.trim() || "Custom code";
        body.saleCode = saleCode;
        body.salePercentage = salePercentage ? Number(salePercentage) : null;
      }
      // Flows generate a CONTAINER + N tailored emails and open the sequence
      // view; campaigns stay single emails that open the editor directly.
      // Raw fetch so a 422 voiceBriefError (the W1 Brand Voice Brief workflow
      // failed) surfaces its specific, actionable message instead of being
      // folded into apiRequest's generic "422: …" error string.
      const postJson = async (url: string) => {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          credentials: "include",
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const err = new Error(
            data?.message || `Generation failed (${res.status})`,
          ) as Error & {
            status?: number;
            serverMessage?: string;
            voiceBriefError?: boolean;
          };
          err.status = res.status;
          if (typeof data?.message === "string" && data.message) {
            err.serverMessage = data.message;
          }
          if (data?.voiceBriefError) err.voiceBriefError = true;
          throw err;
        }
        return res.json();
      };
      if (emailType === "flow") {
        body.emailCount = Number(emailCount);
        // Flows defer per-email template selection: the server creates stub
        // emails (copyStatus "awaiting_template") and each one generates only
        // when the user picks its template (Classic or a preset) on the flow
        // page — nothing renders up front.
        body.deferTemplateSelection = true;
        const flow = (await postJson(
          `/api/brands/${brandId}/email-express/flows/generate`,
        )) as {
          id: string;
          heroCopyWarning?: string | null;
          sellingPointsCopyWarning?: string | null;
          didYouKnowCopyWarning?: string | null;
          logoTaglineCopyWarning?: string | null;
        };
        return {
          kind: "flow" as const,
          id: flow.id,
          heroCopyWarning: flow.heroCopyWarning ?? null,
          sellingPointsCopyWarning: flow.sellingPointsCopyWarning ?? null,
          didYouKnowCopyWarning: flow.didYouKnowCopyWarning ?? null,
          logoTaglineCopyWarning: flow.logoTaglineCopyWarning ?? null,
        };
      }
      const email = (await postJson(
        `/api/brands/${brandId}/email-express/generate`,
      )) as EmailRow & {
        heroCopyWarning?: string | null;
        sellingPointsCopyWarning?: string | null;
        didYouKnowCopyWarning?: string | null;
        logoTaglineCopyWarning?: string | null;
      };
      return {
        kind: "email" as const,
        id: email.id,
        heroCopyWarning: email.heroCopyWarning ?? null,
        sellingPointsCopyWarning: email.sellingPointsCopyWarning ?? null,
        didYouKnowCopyWarning: email.didYouKnowCopyWarning ?? null,
        logoTaglineCopyWarning: email.logoTaglineCopyWarning ?? null,
      };
    },
    onMutate: () => {
      setGenerateError(null);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/flows`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
      // Non-blocking: the email/flow was created with templated copy, but an AI
      // copy workflow (hero and/or selling points) reported missing inputs — let
      // the user know so they can fill in the gaps and regenerate that block.
      if (result.heroCopyWarning) {
        toast({
          title: "AI hero copy skipped",
          description: result.heroCopyWarning,
          variant: "default",
        });
      }
      if (result.sellingPointsCopyWarning) {
        toast({
          title: "AI selling points skipped",
          description: result.sellingPointsCopyWarning,
          variant: "default",
        });
      }
      if (result.didYouKnowCopyWarning) {
        toast({
          title: "AI did-you-know skipped",
          description: result.didYouKnowCopyWarning,
          variant: "default",
        });
      }
      if (result.logoTaglineCopyWarning) {
        toast({
          title: "AI logo tagline skipped",
          description: result.logoTaglineCopyWarning,
          variant: "default",
        });
      }
      if (result.kind === "flow") {
        navigate(`/brands/${brandId}/email-express/flows/${result.id}`);
      } else {
        navigate(`/brands/${brandId}/email-express/${result.id}`);
      }
    },
    onError: (e: any) => {
      // Session expired: say so plainly and flip the page into the re-login
      // banner state instead of the generic failure text.
      if (e?.status === 401) {
        setSessionExpired(true);
        setGenerateError(
          "Your session has expired — please log in again to keep generating.",
        );
        queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        return;
      }
      // The W1 voice-brief block carries a specific, actionable message (e.g.
      // missing brand inputs); surface it verbatim so the user can fix the gap.
      if (e?.voiceBriefError && e?.message) {
        setGenerateError(e.message);
        return;
      }
      // Every other failure: surface the server's actual message with its HTTP
      // status so "Invalid product" / "Forbidden" isn't hidden behind one
      // generic string. No status = network-level failure; keep the generic copy.
      if (typeof e?.status === "number") {
        setGenerateError(
          e?.serverMessage
            ? `${e.serverMessage} (HTTP ${e.status})`
            : `Generation failed (HTTP ${e.status}) — please try again.`,
        );
      } else {
        setGenerateError(
          e?.message || "Generation failed — please try again.",
        );
      }
    },
  });

  // ---- Delete a standalone email (confirmation dialog holds the target) ----
  const [deleteTarget, setDeleteTarget] = useState<EmailRow | null>(null);
  const deleteMutation = useMutation({
    mutationFn: async (emailId: string) => {
      await apiRequest(
        "DELETE",
        `/api/brands/${brandId}/email-express/emails/${emailId}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
      setDeleteTarget(null);
      toast({ title: "Email deleted" });
    },
    onError: (e: any) => {
      setDeleteTarget(null);
      toast({ title: "Delete failed", description: e?.message || "Try again", variant: "destructive" });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (emailId: string) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${emailId}/duplicate`,
        {},
      );
      return (await res.json()) as EmailRow;
    },
    onSuccess: (email) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
      setHighlightId(email.id);
      setTimeout(() => setHighlightId((cur) => (cur === email.id ? null : cur)), 4000);
      toast({ title: "Email duplicated", description: email.name });
    },
    onError: (e: any) => {
      toast({ title: "Duplicate failed", description: e?.message || "Try again", variant: "destructive" });
    },
  });

  // ---- Send to Klaviyo (from the list, no editor) -------------------------
  // Fetch the full email + brand, render and capture the blocks off-screen
  // (identical to an editor send), then push to Klaviyo. `force` is the
  // master-only dev re-send for an already-sent email.
  const sendMutation = useMutation({
    mutationFn: async ({ id, force }: { id: string; force?: boolean }) => {
      const detailRes = await apiRequest(
        "GET",
        `/api/brands/${brandId}/email-express/emails/${id}`,
      );
      const detail = (await detailRes.json()) as EmailDetail;
      const brandRes = await apiRequest("GET", `/api/brands/${brandId}`);
      const brandFull = await brandRes.json();
      return await captureAndSendEmail({
        brandId: brandId!,
        email: detail,
        brand: brandFull,
        force,
      });
    },
    onMutate: ({ id }) => setSendingId(id),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
      const mode = updated.klaviyo?.mode;
      const isFlow = mode === "flow";
      const isTemplate = mode === "template" || mode === "flow_template";
      toast({
        title: isFlow
          ? "Draft flow created in Klaviyo"
          : isTemplate
            ? "Saved to Klaviyo"
            : "Sent to Klaviyo",
        description:
          updated.klaviyo?.message ||
          (isFlow
            ? "A draft flow was created in Klaviyo — review the trigger and turn it on."
            : isTemplate
              ? "Saved as a reusable Klaviyo template."
              : "A draft campaign was created in Klaviyo."),
      });
    },
    onError: (e: any) => {
      toast({ title: "Send failed", description: e?.message || "Try again", variant: "destructive" });
    },
    onSettled: () => setSendingId(null),
  });

  // ---- Send a whole flow to Klaviyo (from the library) --------------------
  // The flow build + Klaviyo chain runs server-side in a worker; this just
  // claims the flow and gets back a pending status. A notification fires on
  // completion. We optimistically flip the row to "sending" via a refetch.
  const flowSendMutation = useMutation({
    mutationFn: async (flowId: string) => {
      // Pre-capture every member email's preview sections BEFORE claiming the
      // send, so the server worker builds each email from the exact editor
      // preview (polished emails skip capture and send their polished stack).
      const flow = flows.find((f) => f.id === flowId);
      await precaptureFlowEmails({
        brandId: brandId!,
        emailIds: (flow?.emails ?? []).map((e) => e.id),
      });
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/flows/${flowId}/send`,
        {},
      );
      return (await res.json()) as { status?: string; klaviyo?: { message?: string } };
    },
    onMutate: (flowId) => setSendingId(flowId),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/flows`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
      toast({
        title: "Sending flow to Klaviyo",
        description:
          updated.klaviyo?.message ||
          "Building your flow and sending it to Klaviyo — you'll get a notification when it's ready.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Send failed", description: e?.message || "Try again", variant: "destructive" });
    },
    onSettled: () => setSendingId(null),
  });

  // ---- Filtered / sorted library ------------------------------------------
  // Campaigns list as individual emails; flows list as ONE grouped container
  // entry (its child emails are hidden from the flat list). Both kinds share
  // the same filters/search/sort so the library reads as one timeline.
  const libraryItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    type LibItem =
      | { kind: "campaign"; id: string; createdAt?: string | null; email: EmailRow }
      | { kind: "flow"; id: string; createdAt?: string | null; flow: FlowRow };

    const items: LibItem[] = [];

    if (filter !== "flow") {
      for (const e of emails) {
        if (e.type !== "campaign") continue;
        if (filter === "sent" && e.status !== "sent_to_klaviyo") continue;
        if (filter === "draft" && e.status !== "draft") continue;
        if (q) {
          const tag = e.narrativeAngleLabel || "";
          const hay = `${e.name} ${e.personaNameSnapshot || ""} ${tag}`.toLowerCase();
          if (!hay.includes(q)) continue;
        }
        items.push({ kind: "campaign", id: e.id, createdAt: e.createdAt, email: e });
      }
    }

    // Flows come from their own (small) unpaginated query; to avoid repeating
    // the same flow rows on every page of campaigns, they only join the list
    // on page 1 — unless the Flow filter is active, which shows them all.
    if (filter !== "campaign" && (page === 1 || filter === "flow")) {
      for (const f of flows) {
        const isSent = f.status === "sent_to_klaviyo";
        if (filter === "sent" && !isSent) continue;
        if (filter === "draft" && isSent) continue;
        if (q) {
          const tag = FLOW_LABELS[f.flowType] || "";
          const persona = f.emails[0]?.personaNameSnapshot || "";
          const hay = `${f.name} ${persona} ${tag}`.toLowerCase();
          if (!hay.includes(q)) continue;
        }
        items.push({ kind: "flow", id: f.id, createdAt: f.createdAt, flow: f });
      }
    }

    items.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return newestFirst ? tb - ta : ta - tb;
    });
    return items;
  }, [emails, flows, search, filter, newestFirst, page]);

  // Picking a saved sale pre-fills the code/percentage/description inputs.
  // Percentage-off sales fill the percentage; every other sale type fills the
  // free-text offer wording from its discount value / promotion copy (the
  // fields stay editable afterwards).
  useEffect(() => {
    if (!saleChoice.startsWith(SAVED_SALE_PREFIX)) return;
    const s = savedSales.find(
      (x) => x.id === saleChoice.slice(SAVED_SALE_PREFIX.length),
    );
    if (!s) return;
    setSaleCode(s.promoCode?.trim() || "");
    if (s.saleType === "percentage_off") {
      const n = parseInt((s.discountValue || "").replace(/[^0-9]/g, ""), 10);
      setSalePercentage(Number.isFinite(n) && n > 0 ? String(n) : "");
      setSaleDescription("");
    } else {
      setSalePercentage("");
      setSaleDescription(
        (
          s.discountValue ||
          s.customerFacingCopy ||
          s.promotionSummary ||
          s.name ||
          ""
        ).trim(),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleChoice]);

  const isGenerating = generateMutation.isPending;

  const filterButtons: { key: typeof filter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "campaign", label: "Campaign" },
    { key: "flow", label: "Flow" },
    { key: "sent", label: "Sent to Klaviyo" },
    { key: "draft", label: "Draft" },
  ];

  const saleOptions: PickerOption[] = [
    { value: NONE_SALE, label: "No sale — render at full price", description: null },
    { value: SALE_20, label: "20% off sitewide", description: "Adds an urgency banner + strikethrough pricing" },
    { value: SALE_CUSTOM, label: "Custom offer…", description: "Enter a code, percentage, and/or free-text offer" },
    ...savedSales.map((s) => ({
      value: `${SAVED_SALE_PREFIX}${s.id}`,
      label: s.name,
      description: s.discountValue || s.promotionSummary || null,
    })),
  ];

  return (
    <TooltipProvider>
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="text-page-title">
              <Mail className="h-6 w-6 text-primary" />
              Email Express
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Pick a product — add a persona and angle if you like. We draft the email — copy, blocks, and subject lines.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFooterSettingsOpen(true)}
              data-testid="button-footer-settings"
            >
              <PanelBottom className="h-3.5 w-3.5 mr-1.5" />
              Footer settings
            </Button>
            {brandId && <KlaviyoConnectionBadge brandId={brandId} />}
            {brand?.name && (
              <span className="text-sm text-muted-foreground" data-testid="text-brand-name">
                Brand: <span className="font-semibold text-foreground">{brand.name}</span>
              </span>
            )}
          </div>
        </div>

        {/* Stale-session banner: a 401 from the generate mutation or a page
            query means the cookie expired while cached data keeps the page
            looking functional. Make it obvious and offer a path back in. */}
        {sessionExpired && (
          <Card
            className="p-4 border-destructive/40 bg-destructive/5 flex items-center justify-between gap-4 flex-wrap"
            data-testid="banner-session-expired"
          >
            <p className="text-sm text-destructive font-medium">
              Your session has expired — please log in again to continue.
            </p>
            <Button
              size="sm"
              onClick={() => {
                window.location.href = "/login";
              }}
              data-testid="button-session-relogin"
            >
              Log in again
            </Button>
          </Card>
        )}

        {/* Top-level tabs: draft a new email vs. manage the Universal library.
            Universal library is feature-flagged off for now. */}
        {SHOW_UNIVERSAL_LIBRARY && (
        <div
          className="inline-flex items-center rounded-lg border border-border/60 bg-muted/40 p-0.5"
          data-testid="toggle-view"
        >
          <button
            type="button"
            onClick={() => setActiveTab("generate")}
            className={[
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              activeTab === "generate"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            ].join(" ")}
            data-testid="tab-generate"
          >
            <Wand2 className="h-3.5 w-3.5" /> Generate
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("universal")}
            className={[
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              activeTab === "universal"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            ].join(" ")}
            data-testid="tab-universal"
          >
            <Library className="h-3.5 w-3.5" /> Universal library
          </button>
        </div>
        )}

        {SHOW_UNIVERSAL_LIBRARY && activeTab === "universal" && brandId && <UniversalLibrary brandId={brandId} />}

        {activeTab === "generate" && (
        <>
        {/* Generator panel */}
        <Card className="relative overflow-hidden p-6 space-y-5 border-border/60 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_-12px_rgba(0,0,0,0.6)] bg-gradient-to-b from-card to-muted/30 dark:from-card dark:to-background/40">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/[0.06] blur-3xl"
          />

          {/* Header row: title + type toggle */}
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 shadow-sm">
                <Wand2 className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">Set your inputs</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Only a product is required — persona, angle, and sale are optional
                </p>
              </div>
            </div>
            <div
              className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60 backdrop-blur shadow-sm"
              data-testid="toggle-email-type"
            >
              <Button
                type="button"
                size="sm"
                variant={emailType === "campaign" ? "default" : "ghost"}
                className="h-7 px-3 text-[11px] gap-1 rounded-full"
                onClick={() => {
                  setEmailType("campaign");
                }}
                data-testid="toggle-type-campaign"
              >
                <Megaphone className="h-3 w-3" /> Campaign
              </Button>
              <Button
                type="button"
                size="sm"
                variant={emailType === "flow" ? "default" : "ghost"}
                className="h-7 px-3 text-[11px] gap-1 rounded-full"
                onClick={() => {
                  setEmailType("flow");
                }}
                data-testid="toggle-type-flow"
              >
                <Repeat className="h-3 w-3" /> Flow
              </Button>
            </div>
          </div>

          {/* Preset template picker — campaign-only, master-gated (the query
              never runs for non-masters). "No template" keeps the existing
              archetype-driven flow byte-identical. */}
          {emailType === "campaign" && isMaster && presetTemplates.length > 0 && (
            <div className="relative space-y-3">
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  1
                </div>
                <div>
                  <h3 className="text-sm font-semibold leading-tight">Template</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {ALLOW_FREEFORM
                      ? "Pick a fixed design, or leave on freeform to let AI choose the layout."
                      : "Pick a template — every email starts from one."}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
                {ALLOW_FREEFORM && (
                <button
                  type="button"
                  onClick={() => setPresetKey("")}
                  data-testid="preset-template-none"
                  className={[
                    "flex flex-col rounded-xl border p-2 text-left transition-all duration-150",
                    !presetKey
                      ? "border-primary/40 bg-gradient-to-b from-primary/[0.08] to-primary/[0.02] ring-1 ring-primary/20"
                      : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
                  ].join(" ")}
                >
                  <div className="aspect-[3/4] w-full rounded-lg bg-muted/60 ring-1 ring-border/60 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-muted-foreground/60" />
                  </div>
                  <div className="mt-1.5 text-xs font-semibold leading-tight">Freeform</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
                    AI picks the layout
                  </div>
                </button>
                )}
                {presetTemplates.map((t) => {
                  const on = presetKey === t.key;
                  const art = t.thumbnailUrl || t.referenceImageUrl;
                  return (
                    // Wrapper (not a button) so the Preview control can be a
                    // real nested <button> without invalid button nesting.
                    <div key={t.key} className="group relative">
                      <button
                        type="button"
                        onClick={() => {
                          // Clicking the card SELECTS it (toggles off if it is
                          // already selected). Previewing is a separate action
                          // via the Preview control below.
                          if (on) {
                            setPresetKey("");
                            setPresetInputs({});
                          } else {
                            setPresetKey(t.key);
                            setPresetInputs({});
                          }
                        }}
                        data-testid={`preset-template-${t.key}`}
                        aria-pressed={on}
                        className={[
                          "flex w-full flex-col rounded-xl border p-2 text-left transition-all duration-150",
                          on
                            ? "border-primary/40 bg-gradient-to-b from-primary/[0.08] to-primary/[0.02] ring-1 ring-primary/20"
                            : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
                        ].join(" ")}
                      >
                        <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-muted/60 ring-1 ring-border/60">
                          {Array.isArray(t.blocks) && t.blocks.length > 0 ? (
                            // Block-driven templates sketch their email
                            // skeleton — one pill per block in sequence
                            // (Email V2 picker parity).
                            <div className="h-full w-full p-2 flex flex-col gap-1 justify-center">
                              {t.blocks.slice(0, 8).map((bk, i) => (
                                <div
                                  key={`${bk}-${i}`}
                                  className={[
                                    "rounded px-1.5 py-[3px] text-[8px] font-semibold uppercase tracking-wide truncate transition-colors",
                                    on
                                      ? "bg-primary/15 text-primary"
                                      : "bg-background/80 text-muted-foreground border border-border/40 group-hover:text-foreground",
                                  ].join(" ")}
                                >
                                  {bk.replace(/_/g, " ")}
                                </div>
                              ))}
                              {t.blocks.length > 8 && (
                                <div className="text-[8px] text-muted-foreground/70 font-medium px-1.5">
                                  +{t.blocks.length - 8} more
                                </div>
                              )}
                            </div>
                          ) : art ? (
                            // The reference is a full, very tall phone/email
                            // mockup — the top ~10% is client chrome (status
                            // bar, subject, sender). Statically frame the
                            // email's actual first section (logo + hero) by
                            // dropping past that chrome with object-position;
                            // no hover panning.
                            <img
                              src={art}
                              alt={t.name}
                              loading="lazy"
                              className="h-full w-full object-cover object-[50%_12%]"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center">
                              <ImageIcon className="h-5 w-5 text-muted-foreground/60" />
                            </div>
                          )}
                          {on && (
                            <div
                              className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm ring-2 ring-background"
                              data-testid={`indicator-selected-${t.key}`}
                            >
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                        <div className="mt-1.5 text-xs font-semibold leading-tight">{t.name}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                          {t.useCase}
                        </div>
                      </button>
                      {/* Preview control — opens the large popup preview
                          without changing the selection. Revealed on hover;
                          always focusable for keyboard/touch users. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewTemplate(t);
                        }}
                        data-testid={`button-preview-${t.key}`}
                        aria-label={`Preview ${t.name}`}
                        className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-md bg-background/90 px-1.5 py-1 text-[10px] font-medium text-foreground shadow-sm ring-1 ring-border/60 backdrop-blur-sm opacity-0 transition-opacity duration-150 hover:bg-background focus-visible:opacity-100 group-hover:opacity-100"
                      >
                        <Eye className="h-3 w-3" /> Preview
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Template popup preview (Task #1032) — large reference image
                  + name/description with an explicit confirm action. The
                  legacy inline per-template inputs UI is removed. */}
              <Dialog
                open={!!previewTemplate}
                onOpenChange={(open) => {
                  if (!open) setPreviewTemplate(null);
                }}
              >
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  {previewTemplate && (
                    <>
                      <DialogHeader>
                        <DialogTitle data-testid="text-template-preview-name">
                          {previewTemplate.name}
                        </DialogTitle>
                        <DialogDescription data-testid="text-template-preview-description">
                          {previewTemplate.description || previewTemplate.useCase}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="rounded-xl overflow-hidden ring-1 ring-border/60 bg-muted/40">
                        {previewTemplate.referenceImageUrl ||
                        previewTemplate.thumbnailUrl ? (
                          <img
                            src={
                              previewTemplate.referenceImageUrl ||
                              previewTemplate.thumbnailUrl ||
                              undefined
                            }
                            alt={previewTemplate.name}
                            className="w-full h-auto"
                            data-testid="img-template-preview"
                          />
                        ) : Array.isArray(previewTemplate.blocks) &&
                          previewTemplate.blocks.length > 0 ? (
                          // Block-driven templates: sketch the email skeleton
                          // (one band per block, in sequence).
                          <div
                            className="mx-auto max-w-sm flex flex-col gap-2 p-6"
                            data-testid="blocks-template-preview"
                          >
                            {previewTemplate.blocks.map((bk, i) => (
                              <div
                                key={`${bk}-${i}`}
                                className="rounded-md border border-border/50 bg-background/80 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                              >
                                {bk.replace(/_/g, " ")}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center py-16">
                            <ImageIcon className="h-8 w-8 text-muted-foreground/60" />
                          </div>
                        )}
                      </div>
                      <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setPreviewTemplate(null)}
                          data-testid="button-template-preview-cancel"
                        >
                          Close
                        </Button>
                        {presetKey === previewTemplate.key ? (
                          // Already selected (via the card) — the popup is just
                          // a preview; offer to deselect from here too.
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                              setPresetKey("");
                              setPresetInputs({});
                              setPreviewTemplate(null);
                            }}
                            data-testid="button-template-preview-confirm"
                          >
                            <Check className="mr-1.5 h-4 w-4" /> Selected
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            onClick={() => {
                              setPresetKey(previewTemplate.key);
                              setPresetInputs({});
                              setPreviewTemplate(null);
                            }}
                            data-testid="button-template-preview-confirm"
                          >
                            Use this template
                          </Button>
                        )}
                      </DialogFooter>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* ---- Step 2: Inputs (numbered stepper, mockup parity) --------- */}
          <div
            className="relative space-y-4 pt-5 border-t border-border/50"
            data-testid="card-campaign-direction"
          >
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                2
              </div>
              <div>
                <h3 className="text-sm font-semibold leading-tight">Inputs</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Tell us what to create.</p>
              </div>
            </div>

          {/* Flow type — only for flow emails; sets the Klaviyo trigger. */}
          {emailType === "flow" && (
            <div className="relative space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Repeat className="h-3.5 w-3.5" /> Flow type
              </Label>
              <Select value={flowType} onValueChange={setFlowType}>
                <SelectTrigger className="w-full md:w-72" data-testid="select-flow-type">
                  <SelectValue placeholder="Choose a flow type" />
                </SelectTrigger>
                <SelectContent>
                  {FLOW_TYPE_VALUES.map((v) => (
                    <SelectItem key={v} value={v} data-testid={`flow-type-${v}`}>
                      {FLOW_LABELS[v]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Sets the Klaviyo trigger when this flow is sent as a draft Flow.
              </p>
              <div className="pt-2 space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Hash className="h-3.5 w-3.5" /> Number of emails
                </Label>
                <Select value={emailCount} onValueChange={setEmailCount}>
                  <SelectTrigger className="w-full md:w-72" data-testid="select-email-count">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`email-count-${n}`}>
                        {n} email{n === 1 ? "" : "s"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  We'll draft each email tailored to its place in the sequence. You can add, remove, and reorder afterward.
                </p>
              </div>
            </div>
          )}

          {/* Core inputs — only product is required. Two-up grid (mockup):
              product | persona on the first row. */}
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3">
            <PickerSelect
              icon={<Package className="h-4 w-4" />}
              label="Product / focus"
              placeholder="Choose product"
              value={productId}
              onChange={(v) => {
                setProductId(v);
                setAngleId("");
              }}
              options={[...products]
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                .map((p) => {
                  const first = Array.isArray(p.mediaJson) && p.mediaJson.length > 0 ? p.mediaJson[0] : null;
                  return { value: p.id, label: p.name, imageUrl: first };
                })}
              testId="select-product"
              searchable
              clearable
            />
            <PickerSelect
              icon={<Users className="h-4 w-4" />}
              label="Persona (optional)"
              placeholder="Brand's general voice"
              value={personaId}
              onChange={(v) => {
                setPersonaId(v);
                setAngleId("");
              }}
              options={personas.map((p) => ({ value: p.id, label: p.name, imageUrl: p.imageUrl ?? null }))}
              testId="select-persona"
              searchable
              clearable
            />
          </div>

          {/* Narrative angle + sale — second row of the Inputs grid (mockup).
              Angle keeps its saved/custom toggle; both optional. */}
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <div className="space-y-2">
            <div
              className="inline-flex items-center rounded-lg border border-border/60 bg-muted/40 p-0.5"
              data-testid="toggle-angle-mode"
            >
              <button
                type="button"
                onClick={() => setAngleMode("saved")}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                  angleMode === "saved"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
                data-testid="toggle-angle-saved"
              >
                <BookOpen className="h-3.5 w-3.5" /> Saved angle
              </button>
              <button
                type="button"
                onClick={() => setAngleMode("custom")}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                  angleMode === "custom"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
                data-testid="toggle-angle-custom"
              >
                <Sparkles className="h-3.5 w-3.5" /> Custom concept
              </button>
            </div>

            {angleMode === "saved" ? (
              <PickerSelect
                icon={<Sparkles className="h-4 w-4" />}
                label="Choose angle"
                placeholder={anglePlaceholder}
                value={angleId}
                onChange={setAngleId}
                groups={angleGroups}
                testId="select-angle"
                searchable
                wrapDescription
                disabled={!productId || availableAngles.length === 0}
                clearable
              />
            ) : (
              <div>
                <Label htmlFor="input-topic" className="text-sm font-medium">
                  Custom concept
                </Label>
                <p className="text-xs text-muted-foreground mb-1.5">
                  Describe the moment, idea, or angle you want the email to land.
                </p>
                <Textarea
                  id="input-topic"
                  placeholder="e.g., A busy mom discovers a 5-minute skincare routine that actually works…"
                  value={topicText}
                  onChange={(e) => setTopicText(e.target.value)}
                  rows={4}
                  className="resize-none"
                  data-testid="input-topic"
                />
              </div>
            )}
          </div>

          {/* Sale / discount — secondary, optional. Top margin aligns it with
              the Choose Angle dropdown (below the saved/custom toggle). */}
          <div className="md:mt-11">
            <PickerSelect
              icon={<Percent className="h-4 w-4" />}
              label="Sale / discount"
              placeholder="Optional — weave in a promo"
              value={saleChoice}
              onChange={setSaleChoice}
              options={saleOptions}
              testId="select-sale"
              wrapDescription
              subtle
            />
          </div>
          </div>

          {/* Custom / saved sale inputs — code, percentage, and free-text offer
              description are each optional and combine ("20% off + free bundle
              with any sandwich"). */}
          {(saleChoice === SALE_CUSTOM || saleChoice.startsWith(SAVED_SALE_PREFIX)) && (
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70">
                  Discount code
                </label>
                <Input
                  value={saleCode}
                  onChange={(e) => setSaleCode(e.target.value)}
                  placeholder="e.g. SAVE20"
                  data-testid="input-sale-code"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70">
                  Percentage off
                </label>
                <Input
                  type="number"
                  value={salePercentage}
                  onChange={(e) => setSalePercentage(e.target.value)}
                  placeholder="e.g. 20"
                  data-testid="input-sale-percentage"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70">
                  Offer description (optional)
                </label>
                <Input
                  value={saleDescription}
                  onChange={(e) => setSaleDescription(e.target.value)}
                  maxLength={255}
                  placeholder="e.g. Free bundle with any sandwich"
                  data-testid="input-sale-description"
                />
                <p className="text-[10px] text-muted-foreground/60">
                  Works alone or with a percentage — "20% off plus a free bundle
                  with any sandwich."
                </p>
              </div>
            </div>
          )}
          </div>

          {/* ---- Step 3: Output settings (single row, mockup parity) ------ */}
          <div
            className="relative space-y-4 pt-5 border-t border-border/50"
            data-testid="card-output-settings"
          >
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                3
              </div>
              <div>
                <h3 className="text-sm font-semibold leading-tight">
                  Output settings
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground/60">optional</span>
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Where should we include links and how many variations?
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
              {/* Optional default CTA link → generated email's linkConfig. */}
              <div className="space-y-1.5 flex-1 min-w-[16rem]">
                <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70">
                  <Link2 className="h-3 w-3" /> CTA link (optional)
                </label>
                <Input
                  value={ctaLink}
                  onChange={(e) => setCtaLink(e.target.value)}
                  placeholder="https://yourstore.com/product"
                  maxLength={2048}
                  data-testid="input-cta-link"
                />
                <p className="text-[11px] text-muted-foreground/70">
                  Every CTA button links here by default — override per section in the editor.
                </p>
              </div>

              {/* Subject variants — segmented 2 / 4 / 6. */}
              <div className="space-y-1.5">
                <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70">
                  Number of Email Subject Variants
                </span>
                <div
                  className="flex items-center gap-0.5 rounded-lg border border-border bg-muted/40 p-0.5 w-fit"
                  role="group"
                  aria-label="Subject variants"
                  data-testid="group-subject-count"
                >
                  {["2", "4", "6"].map((n) => (
                    <Button
                      key={n}
                      type="button"
                      variant={subjectCount === n ? "secondary" : "ghost"}
                      size="sm"
                      className="h-8 px-3.5 text-xs font-medium"
                      aria-pressed={subjectCount === n}
                      onClick={() => setSubjectCount(n)}
                      data-testid={`button-subject-count-${n}`}
                    >
                      {n}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Email blocks — hidden when a preset-sections template is active
              (presets fix their own ordered section list server-side), but
              KEPT VISIBLE for block-driven templates (Task #1032) where the
              checked blocks strictly drive generation. */}
          {SHOW_BLOCK_PICKER && (!presetActive || presetBlockDriven) && (
          <div className="relative space-y-3 pt-4 border-t border-dashed border-border/50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium text-muted-foreground/70">
                <Layers className="h-3 w-3 text-muted-foreground/70" /> Email blocks
              </span>
              <span className="text-[11px] text-muted-foreground/60">
                Hero, body, and footer are always included — add optional sections below.
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {LOCKED_BLOCKS.map((b) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.key}
                    data-testid={`block-locked-${b.key}`}
                    className="flex items-start gap-2.5 rounded-xl border border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] p-3 ring-1 ring-primary/10"
                  >
                    <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground/90 leading-tight">{b.label}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{b.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {OPTIONAL_BLOCKS.map((b) => {
                const Icon = b.icon;
                const on = selectedBlocks.includes(b.key);
                return (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => toggleBlock(b.key)}
                    data-testid={`block-optional-${b.key}`}
                    className={[
                      "flex items-start gap-2.5 text-left rounded-xl border p-3 transition-all duration-150",
                      on
                        ? "border-primary/40 bg-gradient-to-b from-primary/[0.08] to-primary/[0.02] ring-1 ring-primary/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                        : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        "h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ring-1 transition-colors",
                        on ? "bg-primary/15 text-primary ring-primary/20" : "bg-muted/70 text-muted-foreground ring-border/60",
                      ].join(" ")}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground/90 leading-tight">{b.label}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{b.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Universal content — attach a saved library asset or leave off.
              Only types with saved blocks are shown; defaults pre-seed each
              picker. Leaving a block off lets the AI draft that section.
              Hidden for preset templates (fixed section structure). */}
          {!presetActive && SHOW_UNIVERSAL_LIBRARY && assets.length > 0 && (
            <div className="relative space-y-3 pt-4 border-t border-dashed border-border/50">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium text-muted-foreground/70">
                  <Library className="h-3 w-3 text-muted-foreground/70" /> Universal content
                </span>
                <span className="text-[11px] text-muted-foreground/60">
                  Reuse a saved block, or leave off to let AI write it.
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {UNIVERSAL_ASSET_TYPE_LIST.filter(
                  ({ type }) => (assetsByType[type] || []).length > 0,
                ).map(({ type, label, icon: Icon }) => {
                  const saved = assetsByType[type] || [];
                  const options = [
                    { value: UNIVERSAL_NONE, label: "None — skip this block", description: null },
                    ...saved.map((a) => ({
                      value: a.id,
                      label: a.isDefault ? `${a.name} (default)` : a.name,
                      description: null,
                    })),
                  ];
                  return (
                    <PickerSelect
                      key={type}
                      icon={<Icon className="h-4 w-4" />}
                      label={label}
                      placeholder="None"
                      value={universalSel[type] || UNIVERSAL_NONE}
                      onChange={(v) => setUniversalSel((s) => ({ ...s, [type]: v }))}
                      options={options}
                      testId={`select-universal-${type}`}
                      subtle
                      wrapDescription
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Bottom bar: project select + selected-template status pill (left)
              + Generate (right). Project lives here, as before the restyle. */}
          <div className="relative flex items-center justify-between gap-3 pt-4 border-t border-border/50 flex-wrap">
            <div className="flex items-center gap-2">
              <Folder className="h-3.5 w-3.5 text-muted-foreground/70" />
              <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground/70">Project</span>
              <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
                <SelectTrigger
                  className="h-8 text-sm border-0 shadow-none bg-transparent gap-8 px-2 focus:ring-0"
                  data-testid="select-project"
                >
                  <SelectValue placeholder="(no project)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(no project)</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`select-project-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Always-visible template indicator beside Generate: makes it
                unmissable whether a preset template is applied or the email
                will be freeform (users previously assumed a template was
                selected when it wasn't). */}
            <Button
              className="gap-2 ml-auto"
              size="lg"
              disabled={!canGenerate || isGenerating}
              onClick={() => generateMutation.mutate()}
              data-testid="button-generate"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />{" "}
                  {emailType === "flow" ? "Generate flow" : "Generate email"}
                </>
              )}
            </Button>
          </div>
          {generateError && (
            <p
              className="text-sm text-destructive text-right"
              data-testid="text-generate-error"
            >
              {generateError}
            </p>
          )}
        </Card>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total emails" value={stats?.total ?? 0} testId="total" icon={Mail} />
          <StatCard label="Sent to Klaviyo" value={stats?.sent ?? 0} testId="sent" icon={Send} accent="text-emerald-500" />
          <StatCard label="Campaigns" value={stats?.campaigns ?? 0} testId="campaigns" icon={Megaphone} />
          <StatCard label="Flows" value={stats?.flows ?? 0} testId="flows" icon={Repeat} accent="text-primary" />
        </div>

        {/* Library controls */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search emails…"
              className="pl-8 h-9"
              data-testid="input-search"
            />
          </div>
          {filterButtons.map((f) => (
            <Button
              key={f.key}
              variant={filter === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setFilter(f.key);
                setPage(1);
              }}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setNewestFirst((v) => !v)}
            data-testid="button-sort"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            {newestFirst ? "Newest first" : "Oldest first"}
          </Button>
        </div>

        {/* Library table */}
        <Card className="overflow-hidden border-border/40">
          <div className="overflow-x-auto">
          <div className="min-w-[760px]">
          <div className="grid grid-cols-[1fr_120px_140px_120px_160px] gap-2 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border/40 bg-muted/30">
            <div>Email</div>
            <div>Type</div>
            <div>Status</div>
            <div>Created</div>
            <div>Actions</div>
          </div>

          {loadingEmails || loadingFlows ? (
            <div className="p-4 space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : libraryItems.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="text-empty-library">
              {emails.length === 0 && flows.length === 0
                ? "No emails yet — generate your first one above."
                : "Nothing matches your filters."}
            </div>
          ) : (
            libraryItems.map((item) => {
              if (item.kind === "flow") {
                const f = item.flow;
                const tag = FLOW_LABELS[f.flowType] || "Flow";
                const isSent = f.status === "sent_to_klaviyo";
                const isSending = f.status === "sending";
                const persona = f.emails[0]?.personaNameSnapshot;
                const count = f.emails.length;
                return (
                  <div
                    key={`flow-${f.id}`}
                    data-testid={`row-flow-${f.id}`}
                    className={`grid grid-cols-[1fr_120px_140px_120px_160px] gap-2 px-4 py-3 items-center border-b border-border/40 last:border-b-0 transition-colors ${
                      highlightId === f.id ? "bg-primary/10" : "hover:bg-muted/20"
                    }`}
                  >
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="font-semibold text-sm truncate text-left hover:underline"
                        onClick={() => navigate(`/brands/${brandId}/email-express/flows/${f.id}`)}
                        data-testid={`text-flow-name-${f.id}`}
                      >
                        {f.name}
                      </button>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <Badge variant="secondary" className="text-[10px] gap-1 font-normal">
                          <Repeat className="h-2.5 w-2.5" />
                          {tag}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] gap-1 font-normal text-muted-foreground">
                          <Mail className="h-2.5 w-2.5" />
                          {count} email{count === 1 ? "" : "s"}
                        </Badge>
                        {persona && (
                          <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
                            {persona}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <Badge className="text-[10px] bg-blue-500/15 text-blue-500 hover:bg-blue-500/15">
                        Flow
                      </Badge>
                    </div>
                    <div>
                      {isSent ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-500" data-testid={`status-flow-${f.id}`}>
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Sent to Klaviyo
                        </span>
                      ) : isSending ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-amber-500" data-testid={`status-flow-${f.id}`}>
                          <Loader2 className="h-3 w-3 animate-spin" /> Sending…
                        </span>
                      ) : f.status === "send_failed" ? (
                        <Badge variant="outline" className="text-[10px] text-destructive border-destructive/40" data-testid={`status-flow-${f.id}`}>
                          Send failed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid={`status-flow-${f.id}`}>
                          Draft
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground" data-testid={`text-created-flow-${f.id}`}>
                      {formatCreated(f.createdAt)}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start gap-1.5 h-7"
                        onClick={() => navigate(`/brands/${brandId}/email-express/flows/${f.id}`)}
                        data-testid={`button-open-flow-${f.id}`}
                      >
                        <Pencil className="h-3 w-3" /> Open
                      </Button>
                      {!isSent && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start gap-1.5 h-7"
                          disabled={isSending || flowSendMutation.isPending}
                          onClick={() => flowSendMutation.mutate(f.id)}
                          data-testid={`button-send-flow-${f.id}`}
                        >
                          {isSending || sendingId === f.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          {isSending || sendingId === f.id ? "Sending…" : "Send to Klaviyo"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              }

              const e = item.email;
              const tag = e.narrativeAngleLabel || "—";
              const isSent = e.status === "sent_to_klaviyo";
              return (
                <div
                  key={e.id}
                  data-testid={`row-email-${e.id}`}
                  className={`grid grid-cols-[1fr_120px_140px_120px_160px] gap-2 px-4 py-3 items-center border-b border-border/40 last:border-b-0 transition-colors ${
                    highlightId === e.id ? "bg-primary/10" : "hover:bg-muted/20"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate" data-testid={`text-email-name-${e.id}`}>
                      {e.name}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-[10px] gap-1 font-normal">
                        <Sparkles className="h-2.5 w-2.5" />
                        {tag}
                      </Badge>
                      {e.personaNameSnapshot && (
                        <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
                          {e.personaNameSnapshot}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <Badge className="text-[10px] bg-primary/15 text-primary hover:bg-primary/15">
                      Campaign
                    </Badge>
                  </div>
                  <div>
                    {isSent ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-500" data-testid={`status-${e.id}`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Sent to Klaviyo
                      </span>
                    ) : e.copyStatus === "pending" ? (
                      <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/40 gap-1" data-testid={`status-${e.id}`}>
                        <Loader2 className="h-2.5 w-2.5 animate-spin" /> Generating
                      </Badge>
                    ) : e.copyStatus === "failed" ? (
                      <Badge variant="outline" className="text-[10px] text-red-600 border-red-500/40" data-testid={`status-${e.id}`}>
                        Failed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid={`status-${e.id}`}>
                        Draft
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground" data-testid={`text-created-${e.id}`}>
                    {formatCreated(e.createdAt)}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-1.5 h-7"
                      onClick={() => navigate(`/brands/${brandId}/email-express/${e.id}`)}
                      data-testid={`button-edit-${e.id}`}
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                    {isSent ? (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          className="w-full justify-start gap-1.5 h-7"
                          disabled={duplicateMutation.isPending}
                          onClick={() => duplicateMutation.mutate(e.id)}
                          data-testid={`button-duplicate-${e.id}`}
                        >
                          <CopyIcon className="h-3 w-3" /> Duplicate
                        </Button>
                        {isMaster && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-start gap-1.5 h-7 border-amber-500/60 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600"
                            disabled={sendMutation.isPending}
                            onClick={() => sendMutation.mutate({ id: e.id, force: true })}
                            title="Development only — pushes a fresh Klaviyo draft. Delete the old template in Klaviyo manually."
                            data-testid={`button-resend-${e.id}`}
                          >
                            {sendingId === e.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RotateCw className="h-3 w-3" />
                            )}
                            {sendingId === e.id ? "Re-sending…" : "Re-send (dev)"}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start gap-1.5 h-7"
                        disabled={sendMutation.isPending}
                        onClick={() => sendMutation.mutate({ id: e.id })}
                        data-testid={`button-send-${e.id}`}
                      >
                        {sendingId === e.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3" />
                        )}
                        {sendingId === e.id ? "Sending…" : "Send to Klaviyo"}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-1.5 h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeleteTarget(e)}
                      data-testid={`button-delete-${e.id}`}
                    >
                      <Trash2 className="h-3 w-3" /> Delete
                    </Button>
                  </div>
                </div>
              );
            })
          )}
          </div>
          </div>
          {/* Task #1741 — pagination footer (server-side pages of campaigns). */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-border/40 bg-muted/20">
              <span className="text-xs text-muted-foreground" data-testid="text-page-info">
                Page {Math.min(page, totalPages)} of {totalPages} · {totalEmails} emails
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7"
                  disabled={page <= 1 || loadingEmails}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="button-prev-page"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7"
                  disabled={page >= totalPages || loadingEmails}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="button-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
        </>
        )}

        {/* Delete confirmation (standalone emails on the dashboard) */}
        <AlertDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
        >
          <AlertDialogContent data-testid="dialog-delete-email">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this email?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget
                  ? `"${deleteTarget.name}" will be permanently deleted. This can't be undone.`
                  : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-delete-email">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
                }}
                data-testid="button-confirm-delete-email"
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Brand-level footer settings (Task #1899). */}
        <Dialog open={footerSettingsOpen} onOpenChange={setFooterSettingsOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Email footer settings</DialogTitle>
              <DialogDescription>
                Shown in every email footer for this brand — new drafts and
                existing ones alike.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label htmlFor="footer-address">Physical address</Label>
                <Textarea
                  id="footer-address"
                  value={footerAddress}
                  onChange={(e) => setFooterAddress(e.target.value)}
                  placeholder={"123 Main St\nSpringfield, CA 90000"}
                  rows={3}
                  data-testid="input-footer-address"
                />
                <p className="text-xs text-muted-foreground">
                  A mailing address is required in marketing emails (CAN-SPAM).
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="footer-unsub">Unsubscribe link</Label>
                <Input
                  id="footer-unsub"
                  value={footerUnsubUrl}
                  onChange={(e) => setFooterUnsubUrl(e.target.value)}
                  placeholder="https://yourstore.com/unsubscribe"
                  data-testid="input-footer-unsubscribe"
                />
                <p className="text-xs text-muted-foreground">
                  Optional — leave blank to use Klaviyo's built-in unsubscribe
                  link, so it's never missing.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setFooterSettingsOpen(false)}
                data-testid="button-footer-settings-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => saveFooterSettingsMutation.mutate()}
                disabled={saveFooterSettingsMutation.isPending}
                data-testid="button-footer-settings-save"
              >
                {saveFooterSettingsMutation.isPending && (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

function StatCard({
  label,
  value,
  testId,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number;
  testId: string;
  icon: React.ComponentType<{ className?: string }>;
  accent?: string;
}) {
  return (
    <Card className="p-4 border-border/40">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${accent || ""}`} data-testid={`text-stat-${testId}`}>
        {value}
      </div>
    </Card>
  );
}

function PickerSelect({
  icon, label, placeholder, value, onChange, options, groups, testId, disabled, searchable, wrapDescription, subtle, clearable,
}: {
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options?: PickerOption[];
  groups?: PickerGroup[];
  testId: string;
  disabled?: boolean;
  searchable?: boolean;
  wrapDescription?: boolean;
  subtle?: boolean;
  clearable?: boolean;
}) {
  const filled = !!value && value !== "__none__";
  const allOptions = useMemo<PickerOption[]>(
    () => (groups ? groups.flatMap((g) => g.options) : (options ?? [])),
    [groups, options],
  );
  const hasAvatars = allOptions.some((o) => !!o.imageUrl);
  const selected = filled ? allOptions.find((o) => o.value === value) : undefined;
  const selectedDescription = selected?.description?.trim() || "";
  const [open, setOpen] = useState(false);
  const { showSearch, query, setQuery, searchInputRef, isSearching, matchesLabel } =
    usePickerSearch(open, allOptions.length, !!searchable);
  const matchesQuery = (o: PickerOption) => matchesLabel(o.label);
  const filteredOptions = isSearching ? (options ?? []).filter(matchesQuery) : (options ?? []);
  const filteredGroups = (groups ?? [])
    .map((g) => ({ label: g.label, options: isSearching ? g.options.filter(matchesQuery) : g.options }))
    .filter((g) => g.options.length > 0);
  const hasAnyResult = groups ? filteredGroups.length > 0 : filteredOptions.length > 0;
  const renderOption = (o: PickerOption) => {
    const desc = o.description?.trim() || "";
    return (
      <SelectItem key={o.value} value={o.value} className="text-xs">
        <div className={["flex gap-2 min-w-0", wrapDescription ? "items-start" : "items-center"].join(" ")}>
          {hasAvatars ? (
            o.imageUrl ? (
              <img
                src={thumbUrl(o.imageUrl, 64)!}
                onError={thumbFallback(o.imageUrl)}
                alt=""
                className="h-6 w-6 rounded-full object-cover shrink-0 border border-border/40"
                loading="lazy"
              />
            ) : (
              <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 border border-border/40 text-[10px] font-semibold text-muted-foreground">
                {o.label.trim().charAt(0).toUpperCase() || "?"}
              </div>
            )
          ) : null}
          <div className="flex flex-col min-w-0 flex-1">
            <span className="truncate">{o.label}</span>
            {desc && (
              <span
                className={[
                  "text-[11px] text-muted-foreground/80 leading-snug mt-0.5",
                  wrapDescription ? "whitespace-normal break-words" : "truncate",
                ].join(" ")}
                data-testid={`text-option-description-${o.value}`}
              >
                {desc}
              </span>
            )}
          </div>
        </div>
      </SelectItem>
    );
  };
  return (
    <div className="relative">
    <Select value={value} onValueChange={onChange} disabled={disabled} open={open} onOpenChange={setOpen}>
      <SelectTrigger
        className={[
          "group h-auto rounded-xl px-3 py-2.5 transition-all duration-150",
          subtle ? "min-h-[60px]" : "min-h-[72px]",
          "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          filled && !subtle
            ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
            : subtle
              ? "border-border/50 bg-background/40 hover:border-primary/30 hover:bg-background/70"
              : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
        ].join(" ")}
        data-testid={testId}
      >
        <div className="flex items-center gap-3 w-full min-w-0">
          <div
            className={[
              "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
              filled && !subtle
                ? "bg-primary/15 text-primary ring-primary/20"
                : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
            ].join(" ")}
          >
            {icon}
          </div>
          <div className="flex flex-col items-start min-w-0 flex-1 text-left">
            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
              {label}
            </span>
            {filled && selected ? (
              <div className="flex flex-col w-full min-w-0">
                <span className="text-sm truncate w-full leading-tight text-foreground font-semibold">
                  {selected.label}
                </span>
                {selectedDescription && (
                  <span
                    className="text-[11px] text-muted-foreground/80 truncate w-full leading-tight mt-0.5"
                    title={wrapDescription ? selectedDescription : undefined}
                  >
                    {selectedDescription}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm truncate w-full leading-tight text-muted-foreground/60 font-normal">
                <SelectValue placeholder={placeholder} />
              </span>
            )}
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]">
        {showSearch && (
          <PickerSearchHeader
            inputRef={searchInputRef}
            query={query}
            onQueryChange={setQuery}
            testId={`input-search-${testId}`}
          />
        )}
        {showSearch && !hasAnyResult && <PickerNoMatches />}
        {groups
          ? filteredGroups.map((g) => (
              <SelectGroup key={g.label}>
                <SelectLabel className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70">
                  {g.label}
                </SelectLabel>
                {g.options.map(renderOption)}
              </SelectGroup>
            ))
          : filteredOptions.map(renderOption)}
      </SelectContent>
    </Select>
      {clearable && filled && !disabled && (
        <button
          type="button"
          aria-label={`Clear ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onChange("");
            setQuery("");
          }}
          className="absolute top-1.5 right-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-muted/70 transition-colors"
          data-testid={`clear-${testId}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

const ALLOW_FREEFORM = false;
